import React, { useContext } from 'react';

import {
  AutopilotActionType,
  isDraftActionItem,
  isIdeaActionItem,
  isInsightActionItem,
  isPostActionItem,
} from 'common/constants/autopilotActions';
import { SyncReviewIntegrationNames } from 'common/constants/autopilotIntegrations';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';

import {
  DuplicateIdeaSuggestion,
  DuplicatePostSuggestion,
  UniqueIdeaSuggestion,
  UniqueInsightSuggestion,
  UniquePostSuggestion,
} from './Suggestion';

import type { Company } from 'common/api/endpoints/companies';
import type {
  ActionItem,
  IdeaActionItem,
  InsightActionItem,
  PostActionItem,
  PostDraftActionItem,
} from 'common/api/endpoints/queue';

type Props = {
  item: ActionItem;
  company: Company;
};

const InsightSuggestedAction = ({ item }: { item: InsightActionItem }) => {
  return <UniqueInsightSuggestion insight={item.sourceInsight} idea={item.targetIdea} />;
};

const IdeaSuggestedAction = ({ item }: { item: IdeaActionItem }) => {
  if (item.actionType === AutopilotActionType.create) {
    const idea = item.createdIdea;
    return <UniqueIdeaSuggestion idea={idea} />;
  } else if (item.actionType === AutopilotActionType.merge) {
    const fromIdea = item.createdIdea;
    const intoIdea = item.targetIdea;

    return <DuplicateIdeaSuggestion fromIdea={fromIdea} intoIdea={intoIdea} />;
  } else if (item.actionType === AutopilotActionType.dismiss) {
    const idea = item.createdIdea;
    return <UniqueIdeaSuggestion idea={idea} />;
  }

  return null;
};

const PostDraftSuggestedAction = ({
  company,
  item,
}: {
  company: Company;
  item: PostDraftActionItem;
}) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);

  const { sourcePostDraft } = item;

  const isReviewSource = Object.values(SyncReviewIntegrationNames).includes(
    item.source as unknown as SyncReviewIntegrationNames,
  );
  const defaultLink = isReviewSource
    ? '/admin/settings/autopilot/feedback-discovery'
    : '/admin/settings/integrations';

  if (!item.duplicatePost) {
    const onClick = () => {
      if (item.createdPost) {
        return router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: item.createdPost.urlName },
        });
      }

      window.open(item.link ?? defaultLink, '_blank', 'noopener,noreferrer');
    };

    return (
      <UniquePostSuggestion
        title={sourcePostDraft.title}
        translation={sourcePostDraft.translation}
        details={sourcePostDraft.details}
        board={item.board}
        author={sourcePostDraft.author}
        created={sourcePostDraft.createdAt}
        workaround={sourcePostDraft.workaround}
        onClick={onClick}
      />
    );
  }

  return (
    <DuplicatePostSuggestion
      sourceType="draft"
      title={sourcePostDraft.title}
      translation={sourcePostDraft.translation}
      details={sourcePostDraft.details}
      author={sourcePostDraft.author}
      created={sourcePostDraft.createdAt}
      board={item.duplicatePost.board}
      duplicatePost={item.duplicatePost}
      company={company}
      workaround={sourcePostDraft.workaround}
      onClickDuplicatePost={() =>
        window.open(item.link ?? defaultLink, '_blank', 'noopener,noreferrer')
      }
      onClickOriginalPost={() =>
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: item.duplicatePost?.urlName },
        })
      }
    />
  );
};

const CannyPostSuggestedAction = ({
  company,
  item,
}: {
  company: Company;
  item: PostActionItem;
}) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);

  const { sourcePost, duplicatePost } = item;

  return (
    <DuplicatePostSuggestion
      sourceType="post"
      title={sourcePost.title}
      translation={sourcePost.translation}
      details={sourcePost.details}
      author={sourcePost.author}
      created={sourcePost.created}
      board={duplicatePost.board}
      duplicatePost={duplicatePost}
      company={company}
      onClickDuplicatePost={() =>
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: item.duplicatePost.urlName },
        })
      }
      onClickOriginalPost={() =>
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: item.duplicatePost.urlName },
        })
      }
    />
  );
};

const AuditLogSuggestionCell = ({ company, item }: Props) => {
  if (isDraftActionItem(item)) {
    return <PostDraftSuggestedAction company={company} item={item} />;
  } else if (isPostActionItem(item)) {
    return <CannyPostSuggestedAction company={company} item={item} />;
  } else if (isIdeaActionItem(item)) {
    return <IdeaSuggestedAction item={item} />;
  } else if (isInsightActionItem(item)) {
    return <InsightSuggestedAction item={item} />;
  }

  return null;
};

export default AuditLogSuggestionCell;
